import type { Material, Object3D } from 'three';

/**
 * Stored material state for restoring after animations
 */
export type MaterialState = {
	transparent: boolean;
	opacity: number;
};

/**
 * Stored shadow state for restoring after animations
 */
export type ShadowState = {
	castShadow: boolean;
	receiveShadow: boolean;
};

/**
 * Global map to store shadow states during animations
 */
const shadowStates = new Map<Object3D, ShadowState>();

/**
 * Global map to store material states during animations
 */
const materialStates = new Map<Material, MaterialState>();

/**
 * Check if an Object3D is a mesh-like object with materials.
 * Uses the `isMesh` property flag instead of `instanceof Mesh` because
 * CSG operations (three-csg-ts) may produce Mesh objects from a different
 * module instance of Three.js, causing `instanceof` to return false even
 * though the object is functionally a Mesh.
 */
const isMeshWithMaterial = (child: Object3D): child is Object3D & { material: Material | Array<Material> } =>
	!!(child as any).isMesh && !!(child as any).material;

/**
 * Extract the material array from a mesh-like object
 */
const getMaterials = (child: Object3D & { material: Material | Array<Material> }): Array<Material> =>
	Array.isArray(child.material) ? child.material : [child.material];

/**
 * Prepare all materials in an object hierarchy for opacity animation
 */
export const prepareMaterialsForFade = (object: Object3D): void => {
	object.traverse((child) => {
		if (isMeshWithMaterial(child)) {
			for (const material of getMaterials(child)) {
				if (!materialStates.has(material)) {
					// Store original state
					materialStates.set(material, {
						transparent: material.transparent,
						opacity: material.opacity
					});
				}
				// Enable transparency for animation
				if (!material.transparent) {
					material.transparent = true;
					material.needsUpdate = true;
				}
			}
		}
	});
};

/**
 * Restore materials to their original transparency state
 */
export const restoreMaterials = (object: Object3D): void => {
	object.traverse((child) => {
		if (isMeshWithMaterial(child)) {
			for (const material of getMaterials(child)) {
				const state = materialStates.get(material);
				if (state) {
					if (material.transparent !== state.transparent) {
						material.transparent = state.transparent;
						material.needsUpdate = true;
					}
					material.opacity = state.opacity;
					materialStates.delete(material);
				}
			}
		}
	});
};

/**
 * Set opacity on all materials in an object hierarchy.
 * Materials named 'Image-arch' are always kept at opacity 0 (transparent archway).
 */
export const setObjectOpacity = (object: Object3D, opacity: number): void => {
	object.traverse((child) => {
		if (isMeshWithMaterial(child)) {
			for (const material of getMaterials(child)) {
				if (material.name === 'Image-arch') {
					material.opacity = 0;
				} else {
					material.opacity = opacity;
				}
			}
		}
	});
};

/**
 * Store shadow state for all meshes in an object hierarchy
 */
export const prepareShadowsForFade = (object: Object3D): void => {
	object.traverse((child) => {
		if ((child as any).isMesh && !shadowStates.has(child)) {
			shadowStates.set(child, {
				castShadow: child.castShadow,
				receiveShadow: child.receiveShadow
			});
		}
	});
};

/**
 * Set shadow casting/receiving on all meshes in an object hierarchy
 */
export const setObjectShadows = (object: Object3D, enabled: boolean): void => {
	object.traverse((child) => {
		if ((child as any).isMesh) {
			child.castShadow = enabled;
			child.receiveShadow = enabled;
		}
	});
};

/**
 * Restore shadow state to original values
 */
export const restoreShadows = (object: Object3D): void => {
	object.traverse((child) => {
		if ((child as any).isMesh) {
			const state = shadowStates.get(child);
			if (state) {
				child.castShadow = state.castShadow;
				child.receiveShadow = state.receiveShadow;
				shadowStates.delete(child);
			}
		}
	});
};

/**
 * Get the material states map (for testing/debugging)
 */
export const getMaterialStates = (): Map<Material, MaterialState> => materialStates;

/**
 * Clear all material states (for cleanup)
 */
export const clearMaterialStates = (): void => {
	materialStates.clear();
};
